import React, { useEffect, useRef, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { useUploadImageMutation } from '../../../redux-toolkits/misc/misc.slice'
import { toast } from 'react-toastify'
import ImageCroppable from './ImageCroppable'

type ApiErrorResponse = {
  success: boolean
  error: string
}

export const ImageUploadPreviewModal = ({
  image,
  open,
  uploadMode,
  cropAspectRatio,
  cropLabel,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  successToast = true,
}: {
  image: File
  open: boolean
  uploadMode?: string
  cropAspectRatio?: number
  cropLabel?: string
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: string) => void
  successToast?: boolean
}) => {
  const [imageSrc, setImageSrc] = useState<string | ArrayBuffer | null>(null)

  const [crop, setCrop] = useState({ x: 50, y: 50, width: 300, height: 300 })
  const [dragging, setDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [croppedImage, setCroppedImage] = useState<string | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const isBanner = uploadMode === 'banner'
  const [uploadImage, { isLoading }] = useUploadImageMutation()

  const [resizing, setResizing] = useState<string | null>(null)

  const initialiseCrop = () => {
    const imageElement = imgRef.current
    if (!imageElement) return

    const width = imageElement.clientWidth
    const height = imageElement.clientHeight

    if (!cropAspectRatio) {
      setCrop({ x: 0, y: 0, width, height: Math.min(height, 280) })
      return
    }

    const imageAspectRatio = width / height
    const cropWidth =
      imageAspectRatio > cropAspectRatio ? height * cropAspectRatio : width
    const cropHeight =
      imageAspectRatio > cropAspectRatio ? height : width / cropAspectRatio

    setCrop({
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    })
  }

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(true)
    const rect = imgRef?.current?.getBoundingClientRect()
    if (rect)
      setDragStart({
        x: e.clientX - rect.left - crop.x,
        y: e.clientY - rect.top - crop.y,
      })
  }

  const onResizeStart = (
    e: React.MouseEvent<HTMLDivElement>,
    handle: string,
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setResizing(handle)
    const rect = imgRef.current?.getBoundingClientRect()
    if (!rect) return
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imgRef?.current || (!dragging && !resizing)) return

    const rect = imgRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    if (dragging) {
      const x = Math.max(
        0,
        Math.min(e.clientX - rect.left - dragStart.x, rect.width - crop.width),
      )
      const y = Math.max(
        0,
        Math.min(e.clientY - rect.top - dragStart.y, rect.height - crop.height),
      )

      setCrop({ ...crop, x, y })
    } else if (resizing) {
      const newCrop = { ...crop }
      const minSize = 50
      const right = crop.x + crop.width
      const bottom = crop.y + crop.height
      const clamp = (value: number, min: number, max: number) =>
        Math.max(min, Math.min(value, max))

      switch (resizing) {
        case 'br':
          newCrop.width = Math.max(
            minSize,
            Math.min(mouseX - crop.x, rect.width - crop.x),
          )
          newCrop.height = Math.max(
            minSize,
            Math.min(mouseY - crop.y, rect.height - crop.y),
          )
          break
        case 'bl':
          newCrop.x = clamp(mouseX, 0, right - minSize)
          newCrop.width = right - newCrop.x
          newCrop.height = Math.max(
            minSize,
            Math.min(mouseY - crop.y, rect.height - crop.y),
          )
          break
        case 'tr':
          newCrop.y = clamp(mouseY, 0, bottom - minSize)
          newCrop.height = bottom - newCrop.y
          newCrop.width = Math.max(
            minSize,
            Math.min(mouseX - crop.x, rect.width - crop.x),
          )
          break
        case 'tl':
          newCrop.x = clamp(mouseX, 0, right - minSize)
          newCrop.y = clamp(mouseY, 0, bottom - minSize)
          newCrop.width = right - newCrop.x
          newCrop.height = bottom - newCrop.y
          break
        case 't':
          newCrop.y = clamp(mouseY, 0, bottom - minSize)
          newCrop.height = bottom - newCrop.y
          break
        case 'b':
          newCrop.height = Math.max(
            minSize,
            Math.min(mouseY - crop.y, rect.height - crop.y),
          )
          break
        case 'r':
          newCrop.width = Math.max(
            minSize,
            Math.min(mouseX - crop.x, rect.width - crop.x),
          )
          break
        case 'l':
          newCrop.x = clamp(mouseX, 0, right - minSize)
          newCrop.width = right - newCrop.x
          break
      }

      setCrop(newCrop)
    }
  }

  const onMouseUp = () => {
    setDragging(false)
    setResizing(null)
  }

  const cropImage = () => {
    if (!isBanner) {
      void handleUpload(image)
      return
    }

    if (!imgRef.current) return

    const canvas = document.createElement('canvas')
    const scaleX = imgRef.current?.naturalWidth / imgRef.current?.width
    const scaleY = imgRef.current?.naturalHeight / imgRef.current?.height

    canvas.width = Math.round(crop.width * scaleX)
    canvas.height = Math.round(crop.height * scaleY)

    const ctx = canvas.getContext('2d', {
      alpha: false,
      willReadFrequently: false,
    })

    if (ctx) ctx.imageSmoothingEnabled = true

    ctx?.drawImage(
      imgRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY,
    )

    // setCroppedImage(canvas.toDataURL('image/png'))
    setCroppedImage(canvas.toDataURL('image/jpeg', 0.92))
  }

  useEffect(() => {
    if (!croppedImage) return

    const uploadCroppedImage = async () => {
      const response = await fetch(croppedImage)
      const blob = await response.blob()
      const croppedFile = new File([blob], 'cropped-image.jpg', {
        type: 'image/jpeg',
      })

      await handleUpload(croppedFile)
    }

    void uploadCroppedImage()
  }, [croppedImage])

  const handleUpload = async (file: File) => {
    const formData = new FormData()
    formData.append('image', file)

    try {
      const response = await uploadImage(formData)

      if (response?.data?.success) {
        if (successToast) {
          toast.success(response?.data?.data.url)
        }
        onSuccessCallback && onSuccessCallback(response?.data?.data.url)
        setOpen(false)
        onCloseCallback && onCloseCallback()
      } else {
        if (response.error) {
          if ('data' in response.error) {
            const err = response.error.data as ApiErrorResponse
            toast.error(err.error)
          } else {
            console.log(response.error)
          }
        }
      }
    } catch (error) {
      console.error('Upload error:', error)
    }
  }

  useEffect(() => {
    const imageUrl = URL.createObjectURL(image)
    setImageSrc(imageUrl)
    setCroppedImage(null)

    return () => URL.revokeObjectURL(imageUrl)
  }, [image])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={onCloseCallback}
      title={isBanner ? 'Crop banner image' : 'Image upload preview'}
    >
      {isBanner ? (
        <div className="mb-4 text-sm text-sec-black">
          <p className="font-medium text-black">
            {cropLabel || 'Position the image inside the crop area'}
          </p>
          <p>
            Drag the frame to reposition it, or move any edge or corner to
            freely choose the area to keep.
          </p>
        </div>
      ) : null}
      <div
        className="flex h-[400px] w-full items-center justify-center overflow-hidden rounded-md bg-gray-100"
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div className="relative inline-flex max-h-full max-w-full overflow-hidden">
          <img
            src={imageSrc as string}
            className="block max-h-[400px] max-w-full object-contain text-center"
            alt="Banner crop preview"
            ref={imgRef}
            onLoad={initialiseCrop}
          />
          {isBanner ? (
            <ImageCroppable
              crop={crop}
              onMouseDown={onMouseDown}
              onResizeStart={onResizeStart}
              resizable
            />
          ) : null}
        </div>
      </div>

      <Button
        label={isBanner ? 'Crop and upload image' : 'Upload image'}
        onClick={cropImage}
        primary
        className="mx-auto mt-6 sm:w-full md:w-[50%]"
        loading={isLoading}
        disabled={isLoading}
      />
    </ModalComponent>
  )
}
